/**
 * 會計核心模組的計價公式(REQ-20260806002)—— 前後端共用的**單一真相**。
 *
 * ⚠️ 這裡的算術用 `number`,只給前端即時試算用。後端寫入 DB 的金額一律由
 * `apps/api/src/common/utils/accounting-amounts.ts` 以 `Prisma.Decimal` 重算,
 * 前端送什麼金額都不採信。兩邊的公式必須一致,api 的
 * `accounting-amounts.spec.ts` 會拿這裡的結果逐案比對,防止兩邊各自漂移。
 *
 * 為什麼不乾脆只留一份:`@app/shared` 不可以依賴 Prisma(見 index.ts 的使用原則),
 * 而金額進 DB 前必須是 Decimal —— 只能兩份實作 + 一個比對測試。
 */

/** 客戶結帳週期。值存在 `customers.settlementCycle`(VARCHAR),null = 未設定。 */
export const SETTLEMENT_CYCLES = {
  /** 當月出貨當月結 —— 對帳單期間 = 該自然月 */
  MONTHLY: '月結',
  /** 次月結 —— 對帳單期間同月結,但到期日再往後推一個月 */
  NEXT_MONTH: '次月結',
  /** 貨到付款 —— 不做月結對帳單 */
  ON_DELIVERY: '貨到付款',
  /** 現金 —— 不做月結對帳單 */
  CASH: '現金',
} as const;

export type SettlementCycle = keyof typeof SETTLEMENT_CYCLES;

export const SETTLEMENT_CYCLE_KEYS = Object.keys(SETTLEMENT_CYCLES) as SettlementCycle[];

/**
 * 帳款對象(REQ-20260810002)。值存在 `sales_orders.payerType`(VARCHAR),
 * 既有單與 mdb 匯入單一律是 `COMPANY`(DB 預設值)。
 * 只有兩個值、使用者不需要維護,所以是應用層常數而不是 reference table。
 */
export const PAYER_TYPES = {
  /** 公司公帳 —— 進客戶月結對帳單 */
  COMPANY: '公司公帳',
  /** 個人私帳 —— 不進月結對帳單,走「個人未結明細」單獨向本人收 */
  PERSONAL: '個人私帳',
} as const;

export type PayerType = keyof typeof PAYER_TYPES;

/** 只有這兩種週期會產生月結對帳單 */
export function isMonthlyCycle(cycle: string | null | undefined): boolean {
  return cycle === 'MONTHLY' || cycle === 'NEXT_MONTH';
}

/** 四捨五入到指定小數位。JS 的 toFixed 對 .005 會因浮點表示往下,先做一次補償。 */
export function roundTo(value: number, digits: number): number {
  const f = 10 ** digits;
  return Math.round((value + Number.EPSILON * Math.abs(value)) * f) / f;
}

export function toNumber(v: string | number | null | undefined): number {
  if (v == null || v === '') return 0;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
}

/** 金額字串:固定兩位小數,對齊 DB 的 DECIMAL(14,2) */
export function toAmountString(v: number): string {
  return roundTo(v, 2).toFixed(2);
}

/**
 * 進貨單價字串:四捨五入到 4 位小數,對齊 `purchase_order_lines.unitPrice` 的
 * DECIMAL(14,4)(REQ-20261001001)。第 3、4 位是 0 就去掉,但至少留兩位 ——
 * 603 ÷ 1000 → '0.603'、3000 ÷ 20 → '150.00'(與改之前的 `toAmountString()` 逐字相同)。
 */
export function toUnitPriceString(v: number): string {
  return roundTo(v, 4).toFixed(4).replace(/0{1,2}$/, '');
}

export interface LineAmountInput {
  qty: string | number | null | undefined;
  /** 單價與總價擇一,兩格都沒填視為 0(zod 的 `.refine()` 才是擋在門口的那道) */
  unitPrice?: string | number | null;
  /**
   * 整批總價(REQ-20260807002 訪談快捷建單、REQ-20260905007 起出貨單品項也能用)。
   * 有填時**總價本身就是計價基數金額**(取代「基數 × 單價」),折扣仍照乘。
   * 單價欄是 DECIMAL(14,2),1000 ÷ 3 存成 333.33 再乘回 3 只有 999.99 ——
   * 談好的金額不該因為除不盡就少一分,所以總價模式下總價才是權威。
   */
  totalPrice?: string | number | null;
  /** 才數(印刷計價基數);null = 這張單不用才數計價 */
  sizeUnits?: string | number | null;
  /** 模數;有才數但沒填模數時視為 1 */
  moldCount?: string | number | null;
  /** 折扣率 0-1(0 = 不打折,沿用既有 SalesOrderLine.discount 的語意) */
  discount?: string | number | null;
}

/**
 * 品項金額。
 *
 * 計價基數:有填才數 → `才數 × 模數`;否則 → `數量`。
 * 印刷業報價常是「一才多少錢、開幾模」,數量(張/件)仍要留著出貨與扣庫存用,
 * 所以兩者並存而不是二選一地覆蓋 qty。
 *
 *   金額 = 基數 × 單價 × (1 - 折扣率)
 *   填總價時 = 總價 × (1 - 折扣率)  ← 見 `totalPrice`
 */
export function calcLineAmount(line: LineAmountInput): string {
  const sizeUnits = line.sizeUnits == null || line.sizeUnits === '' ? null : toNumber(line.sizeUnits);
  const base =
    sizeUnits == null
      ? toNumber(line.qty)
      : sizeUnits * (line.moldCount == null || line.moldCount === '' ? 1 : toNumber(line.moldCount));
  const gross =
    line.totalPrice == null || line.totalPrice === ''
      ? base * toNumber(line.unitPrice)
      : toNumber(line.totalPrice);
  const discount = toNumber(line.discount);
  return toAmountString(gross * (1 - discount));
}

export interface OrderTotalsInput {
  lines: LineAmountInput[];
  /** 版費(印刷版一次性費用) */
  plateFee?: string | number | null;
  /** 刀模費 */
  dieFee?: string | number | null;
  /** 稅率,預設 0.05 */
  taxRate?: string | number | null;
}

export interface OrderTotals {
  /** 品項小計(不含版費 / 刀模費) */
  lineSubtotal: string;
  /** 未稅合計 = 品項小計 + 版費 + 刀模費 */
  subtotal: string;
  tax: string;
  total: string;
}

/**
 * 單頭金額。版費 / 刀模費併入未稅合計一起課稅 —— 它們是銷售價格的一部分,
 * 不是代收代付,發票上也是同一張單的金額。
 */
export function calcOrderTotals(input: OrderTotalsInput): OrderTotals {
  const lineSubtotal = input.lines.reduce((sum, l) => sum + toNumber(calcLineAmount(l)), 0);
  const subtotal = lineSubtotal + toNumber(input.plateFee) + toNumber(input.dieFee);
  const taxRate = input.taxRate == null || input.taxRate === '' ? 0.05 : toNumber(input.taxRate);
  const tax = roundTo(subtotal * taxRate, 2);
  return {
    lineSubtotal: toAmountString(lineSubtotal),
    subtotal: toAmountString(subtotal),
    tax: toAmountString(tax),
    total: toAmountString(roundTo(subtotal, 2) + tax),
  };
}

/**
 * 稅別(REQ-20260806003)。對應 `sales_orders.taxMode`。
 * 「內含」不是某個稅率,是「輸入的金額已經含稅」—— 稅率仍然是 5%。
 */
export type SalesOrderTaxMode = 'EXCLUSIVE' | 'INCLUSIVE' | 'EXEMPT';

export interface TaxModeAmountsInput {
  /** 計價基數金額(數量 × 單價)。外加 / 免稅視為未稅金額,內含視為含稅金額 */
  gross: string | number | null | undefined;
  taxMode?: SalesOrderTaxMode | null;
  /** 稅率,預設 0.05;免稅時一律當 0 */
  taxRate?: string | number | null;
}

export interface TaxModeAmounts {
  subtotal: string;
  tax: string;
  total: string;
}

/**
 * 依稅別拆出未稅 / 稅額 / 含稅總計(REQ-20260806003)。
 *
 *   外加 EXCLUSIVE:未稅 = 輸入金額,稅額 = 未稅 × 稅率,總計 = 未稅 + 稅額
 *   內含 INCLUSIVE:總計 = 輸入金額,未稅 = 總計 ÷ (1 + 稅率),稅額 = 總計 - 未稅
 *   免稅 EXEMPT  :未稅 = 輸入金額,稅額 = 0,總計 = 未稅
 *
 * 內含的稅額用「總計 - 未稅」倒扣而不是「未稅 × 稅率」:後者四捨五入後
 * 三個數字可能加不起來(285.71 + 14.29 才等於 300,但 285.71 × 0.05 = 14.29 純屬巧合)。
 * 發票上的三個金額必須自洽。
 */
export function calcTaxModeAmounts(input: TaxModeAmountsInput): TaxModeAmounts {
  const gross = toNumber(input.gross);
  const mode = input.taxMode ?? 'EXCLUSIVE';
  const rate = input.taxRate == null || input.taxRate === '' ? 0.05 : toNumber(input.taxRate);

  if (mode === 'EXEMPT') {
    const subtotal = roundTo(gross, 2);
    return { subtotal: toAmountString(subtotal), tax: '0.00', total: toAmountString(subtotal) };
  }
  if (mode === 'INCLUSIVE') {
    const total = roundTo(gross, 2);
    const subtotal = roundTo(total / (1 + rate), 2);
    return {
      subtotal: toAmountString(subtotal),
      tax: toAmountString(total - subtotal),
      total: toAmountString(total),
    };
  }
  const subtotal = roundTo(gross, 2);
  const tax = roundTo(subtotal * rate, 2);
  return {
    subtotal: toAmountString(subtotal),
    tax: toAmountString(tax),
    total: toAmountString(subtotal + tax),
  };
}

// ──────────────────────────────────────────────────────────────────────────
// 進貨入帳(REQ-20260813001)
// ──────────────────────────────────────────────────────────────────────────

/** 進貨稅別。對應 `purchase_orders.taxCategory` 與 `purchase_order_lines.taxCategory`。 */
export type PurchaseTaxCategory = 'TAXABLE' | 'ZERO_RATED' | 'EXEMPT';

export const PURCHASE_TAX_CATEGORIES = {
  TAXABLE: '應稅',
  ZERO_RATED: '零稅率',
  EXEMPT: '免稅',
} as const;

/**
 * 品項實際生效的稅別:有逐項覆寫就用覆寫值,否則沿用單頭。
 * `null` 在 line 上是「沿用單頭」而不是「沒有稅別」,所以不能寫成 `?? 'TAXABLE'`。
 */
export function effectiveTaxCategory(
  lineCategory: PurchaseTaxCategory | null | undefined,
  orderCategory: PurchaseTaxCategory | null | undefined,
): PurchaseTaxCategory {
  return lineCategory ?? orderCategory ?? 'TAXABLE';
}

/**
 * 進貨稅別的使用者可見值域(REQ-20260902011)—— token 與廠商主檔的
 * 「稅別」(`suppliers.taxMode`)**完全相同**,不自創第二種拼法。
 *
 * ⚠️ 「含稅」與「稅外加」的差別**不落在 `taxCategory`** 上:兩者在單據上都是應稅,
 * 差的是 `unitPriceTaxInclusive`(輸入的單價含不含稅)。REQ-20260902009 §二 當初
 * 正是因為這個落差,才寫了一支只映到 `taxCategory` 的對照函式(廠商含稅 / 稅外加
 * → 一律應稅)。本次使用者要求兩邊值域一致,做法改成
 * **讓同一顆下拉同時決定 `taxCategory` 與 `unitPriceTaxInclusive` 兩個欄位**,
 * 對照因此變成雙向的 `purchaseTaxModeToFields()` / `purchaseTaxModeFromFields()`。
 */
export const PURCHASE_TAX_MODES = {
  INCLUSIVE: '含稅',
  EXCLUSIVE: '稅外加',
  EXEMPT: '免稅',
} as const;

export type PurchaseTaxMode = keyof typeof PURCHASE_TAX_MODES;

/**
 * 稅別 → 單據上的兩個欄位。
 * 免稅一律非含稅:免稅品項的單價裡本來就沒有稅可以反推(見 `calcPurchaseTotals`)。
 */
export function purchaseTaxModeToFields(mode: PurchaseTaxMode): {
  taxCategory: PurchaseTaxCategory;
  unitPriceTaxInclusive: boolean;
} {
  if (mode === 'EXEMPT') return { taxCategory: 'EXEMPT', unitPriceTaxInclusive: false };
  return { taxCategory: 'TAXABLE', unitPriceTaxInclusive: mode === 'INCLUSIVE' };
}

/**
 * 反查:單據上的兩個欄位 → 稅別。
 *
 * **零稅率回 `null`** —— 廠商主檔沒有這個值,對不回去。呼叫端要自己決定怎麼呈現,
 * 不可以拿 `?? 'EXCLUSIVE'` 之類的預設值蓋過去:零稅率靜默變成應稅會改掉單據金額。
 */
export function purchaseTaxModeFromFields(
  taxCategory: PurchaseTaxCategory,
  unitPriceTaxInclusive: boolean,
): PurchaseTaxMode | null {
  if (taxCategory === 'EXEMPT') return 'EXEMPT';
  if (taxCategory === 'ZERO_RATED') return null;
  return unitPriceTaxInclusive ? 'INCLUSIVE' : 'EXCLUSIVE';
}

/**
 * 庫存單位數量 = 採購數量 × 換算率。換算率沒填視為 1(1 採購單位 = 1 庫存單位)。
 * 對齊 `purchase_order_lines.stockUnitQty` 的 DECIMAL(14,4)。
 */
export function calcStockUnitQty(
  qty: string | number | null | undefined,
  conversionRate?: string | number | null,
): string {
  const rate =
    conversionRate == null || conversionRate === '' ? 1 : toNumber(conversionRate);
  return roundTo(toNumber(qty) * rate, 4).toFixed(4);
}

/**
 * 進貨明細的付款方式(REQ-20260907002 運輸費用單)。
 * 定義在這裡而不是 `purchase-order.schema.ts`,理由同 `PURCHASE_TAX_CATEGORIES` ——
 * **它會改變金額**(見 `calcPurchaseTotals` 對 `COLLECT` 的處理),
 * 與計算公式放在同一個檔案才不會有人改了值域卻漏看算式。
 *
 * `COLLECT`(到付)是由收貨方付錢給貨運行,我方只是把它記在單子上讓現場對得起來 ——
 * 那筆錢從來不是我方的成本,也不會變成應付。
 */
export const PURCHASE_LINE_PAYMENT_TYPES = {
  MONTHLY: '月結',
  CASH: '現付',
  COLLECT: '到付',
} as const;

export type PurchaseLinePaymentType = keyof typeof PURCHASE_LINE_PAYMENT_TYPES;

export interface PurchaseLineInput {
  qty: string | number | null | undefined;
  unitPrice: string | number | null | undefined;
  /** 折扣率 0-1,沿用既有 PurchaseOrderLine.discount 的語意 */
  discount?: string | number | null;
  /** 逐項稅別覆寫;null = 沿用單頭 */
  taxCategory?: PurchaseTaxCategory | null;
  /**
   * 這一列的付款方式(REQ-20260907002)。**只有運輸費用單填得到**,
   * 其餘所有單據(以及所有既有單)一律 `null` / 不帶 —— 行為與改動前逐字相同。
   *
   * `'COLLECT'`(到付)的列**整列排除在單據金額之外**:未稅小計、應稅小計、稅額、
   * 含稅總計都不含它,含稅反推的比對基數(`taxInclusiveDiff`)也不含它。
   * 它仍然會出現在 `lineAmounts` 裡(那是逐列金額,畫面要拿去顯示 / 後端要寫進
   * `purchase_order_lines.amount`)—— 只是不進單頭的加總。
   */
  linePaymentType?: PurchaseLinePaymentType | null;
}

export interface PurchaseTotalsInput {
  lines: PurchaseLineInput[];
  /** 單頭稅別,品項沒覆寫時沿用 */
  taxCategory?: PurchaseTaxCategory | null;
  /** 稅率,預設 0.05 */
  taxRate?: string | number | null;
  /** 輸入的單價是否已含稅 */
  unitPriceTaxInclusive?: boolean | null;
}

export interface PurchaseTotals {
  /** 逐列未稅金額,順序與輸入品項一致 */
  lineAmounts: string[];
  subtotal: string;
  /** 應稅品項的未稅小計 —— 稅額的計算基礎 */
  taxableSubtotal: string;
  tax: string;
  total: string;
  /**
   * 單價含稅時,「使用者輸入的含稅金額」與「反推後再加回稅額」的差(可為負)。
   * 逐列四捨五入 + 稅額進位到整數元本來就會有零頭,±1 元內視為正常;
   * 超出表示這張單的組合不對(例:含稅單價配上免稅品項),要讓使用者看到。
   */
  taxInclusiveDiff: string;
}

/** 含稅合計與輸入金額的容忍值(元)。刻意寫死不做成設定項 —— 它是四捨五入的零頭上限,不是政策。 */
export const PURCHASE_TAX_INCLUSIVE_TOLERANCE = 1;

/**
 * 進貨單金額(REQ-20260813001 R-01 / R-02 / R-11)。
 *
 *   1. 逐列未稅小計 = 數量 × 未稅單價 × (1 - 折扣率),四捨五入到小數 2 位
 *   2. 稅額 = Σ(生效稅別為應稅的未稅小計) × 稅率,**四捨五入到整數元**
 *   3. 含稅合計 = 未稅合計 + 稅額
 *
 * 與出貨單的 `calcOrderTotals()` 有三處不同,所以是另一支函式而不是加參數:
 * 稅額進位到整數元(發票上的稅額是整數)、稅基只算應稅品項、單價可能已含稅。
 *
 * 單價含稅時未稅單價 = 輸入單價 ÷ (1 + 稅率),但**只對應稅品項換算** ——
 * 免稅 / 零稅率的品項價格裡本來就沒有稅,除以 1.05 只會把金額憑空調低。
 *
 * REQ-20260907002 多一條:`linePaymentType === 'COLLECT'`(到付)的列不進加總 ——
 * 那筆錢是收貨方付給貨運行的,不是我方的成本也不會變成應付,只是記在單子上。
 * 沒帶 `linePaymentType` 的列(所有既有單、所有非運費類的單)行為逐字不變。
 */
export function calcPurchaseTotals(input: PurchaseTotalsInput): PurchaseTotals {
  const rate = input.taxRate == null || input.taxRate === '' ? 0.05 : toNumber(input.taxRate);
  const inclusive = input.unitPriceTaxInclusive === true;

  let subtotal = 0;
  let taxableSubtotal = 0;
  let inputGross = 0;
  const lineAmounts: string[] = [];

  for (const line of input.lines) {
    const category = effectiveTaxCategory(line.taxCategory, input.taxCategory);
    const factor = 1 - toNumber(line.discount);
    const gross = toNumber(line.qty) * toNumber(line.unitPrice) * factor;
    const net =
      inclusive && category === 'TAXABLE' ? roundTo(gross / (1 + rate), 2) : roundTo(gross, 2);

    lineAmounts.push(toAmountString(net));

    // 到付的列不進單頭加總(REQ-20260907002)。`linePaymentType` 為 null 的列
    // —— 也就是所有既有單與所有非運費類的單 —— 一律走原本那條路,一個字都沒變
    if (line.linePaymentType === 'COLLECT') continue;

    subtotal = roundTo(subtotal + net, 2);
    if (category === 'TAXABLE') taxableSubtotal = roundTo(taxableSubtotal + net, 2);
    inputGross = roundTo(inputGross + roundTo(gross, 2), 2);
  }

  const tax = roundTo(taxableSubtotal * rate, 0);
  const total = roundTo(subtotal + tax, 2);
  return {
    lineAmounts,
    subtotal: toAmountString(subtotal),
    taxableSubtotal: toAmountString(taxableSubtotal),
    tax: toAmountString(tax),
    total: toAmountString(total),
    taxInclusiveDiff: toAmountString(inclusive ? total - inputGross : 0),
  };
}

/**
 * 單據未結餘額 = 單據總額 - 已沖金額 - 折讓。
 *
 * 折讓一定要扣:折讓是「這筆錢不用收了」,不是收款。少扣的話客戶付完全額仍會
 * 留下一筆永遠結不掉的尾數,月結對帳單就會一直帶著它。
 */
export function calcOutstanding(
  total: string | number | null | undefined,
  settled: string | number | null | undefined,
  allowance?: string | number | null,
): string {
  return toAmountString(toNumber(total) - toNumber(settled) - toNumber(allowance));
}
